import React, { useState, useEffect, useCallback } from "react";
import {
  Snackbar,
  Alert,
  AlertColor,
  AlertTitle,
  IconButton,
  Collapse,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import notificationSnackbarService, {
  NotificationData,
} from "../services/NotificationSnackbarService";
import { useTranslation } from "react-i18next";

/**
 * Компонент для отображения Snackbar уведомлений
 * Должен быть добавлен в корень приложения
 */
const NotificationSnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [currentNotification, setCurrentNotification] =
    useState<NotificationData | null>(null);
  const [notificationQueue, setNotificationQueue] = useState<
    NotificationData[]
  >([]);
  const [audioContextInitialized, setAudioContextInitialized] = useState(false);

  // Инициализация AudioContext при первом взаимодействии пользователя
  useEffect(() => {
    const handleUserInteraction = () => {
      if (!audioContextInitialized) {
        notificationSnackbarService.initAudioContextOnInteraction();
        setAudioContextInitialized(true);
      }
    };
    
    // Слушаем клики и тачи
    document.addEventListener("click", handleUserInteraction, { once: true, passive: true });
    document.addEventListener("touchstart", handleUserInteraction, { once: true, passive: true });
    document.addEventListener("keydown", handleUserInteraction, { once: true, passive: true });
    
    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
    };
  }, [audioContextInitialized]);

  // Обработка нового уведомления
  const handleNotification = useCallback((notification: NotificationData) => {
    setNotificationQueue((prev) => [...prev, notification]);
  }, []);

  // Подписка на уведомления при монтировании
  useEffect(() => {
    const unsubscribe =
      notificationSnackbarService.subscribe(handleNotification);
    return unsubscribe;
  }, [handleNotification]);

  // Показ следующего уведомления из очереди
  useEffect(() => {
    if (!open && notificationQueue.length > 0) {
      const next = notificationQueue[0];
      setCurrentNotification(next);
      setNotificationQueue((prev) => prev.slice(1));
      setOpen(true);
    }
  }, [open, notificationQueue]);

  // Закрытие snackbar
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  // Определение severity для Alert
  const getSeverity = (type: string): AlertColor => {
    switch (type) {
      case "order_available":
      case "order_assigned":
      case "success":
        return "success";
      case "order_cancelled":
      case "error":
        return "error";
      case "warning":
        return "warning";
      case "order_status_changed":
      case "order_completed":
      case "info":
      default:
        return "info";
    }
  };

  // Определение иконки для типа уведомления
  const getIcon = (type: string): string => {
    const icons: Record<string, string> = {
      order_available: "📦",
      order_status_changed: "📋",
      order_assigned: "✅",
      order_cancelled: "❌",
      order_completed: "✨",
      system: "⚙️",
      info: "ℹ️",
      success: "✅",
      warning: "⚠️",
      error: "❌",
    };
    return icons[type] || "🔔";
  };

  if (!currentNotification) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={currentNotification.duration || 60000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Collapse}
        // TransitionProps={{ direction: 'up' }} - Removed due to type incompatibility
        sx={{
          "& .MuiSnackbarContent-root": {
            minWidth: "300px",
            maxWidth: "500px",
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={getSeverity(currentNotification.type)}
          variant="filled"
          sx={{
            width: "100%",
            animation: "slideIn 0.3s ease-out",
            "@keyframes slideIn": {
              from: {
                transform: "translateX(100%)",
                opacity: 0,
              },
              to: {
                transform: "translateX(0)",
                opacity: 1,
              },
            },
          }}
          action={
            currentNotification.actionLabel && currentNotification.onAction ? (
              <>
                <IconButton
                  size="small"
                  aria-label="action"
                  color="inherit"
                  onClick={currentNotification.onAction}
                  sx={{ mr: 1 }}
                >
                  {currentNotification.actionLabel}
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={handleClose}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }
        >
          <AlertTitle sx={{ fontWeight: "bold", fontSize: "1rem" }}>
            {getIcon(currentNotification.type)} {currentNotification.title}
          </AlertTitle>
          {currentNotification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationSnackbarProvider;
